import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, Loader2 } from 'lucide-react';

/**
 * Pagina di login
 */
function LoginPage() {
  const { login, verify2FA } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Stato per 2FA
  const [requires2FA, setRequires2FA] = useState(false);
  const [userId, setUserId] = useState(null);
  const [code2FA, setCode2FA] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // DEBUG: Log email prima dell'invio
    console.log('=== DEBUG LOGIN ===');
    console.log('Email state:', email);
    console.log('Email length:', email.length);
    console.log('Email charCodes:', [...email].map(c => `${c}:${c.charCodeAt(0)}`).join(' '));

    try {
      const result = await login(email, password);

      if (result.requires2FA) {
        setRequires2FA(true);
        setUserId(result.userId);
      }
    } catch (err) {
      console.log('=== DEBUG LOGIN ERROR ===');
      console.log('Error:', err);
      console.log('Response:', err.response);
      console.log('Message:', err.response?.data?.message);
      setError(err.response?.data?.message || 'Errore durante il login');
    } finally {
      console.log('=== DEBUG LOGIN FINALLY ===');
      setIsLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await verify2FA(userId, code2FA);
    } catch (err) {
      setError(err.response?.data?.message || 'Codice non valido');
    } finally {
      setIsLoading(false);
    }
  };

  // Form 2FA
  if (requires2FA) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-6 animate-fadeIn">
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Verifica identità</h2>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            Inserisci il codice a 6 cifre inviato alla tua email
          </p>
        </div>

        <form onSubmit={handle2FASubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="code" className="block text-sm font-medium mb-2">
              Codice di verifica
            </label>
            <input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code2FA}
              onChange={(e) => setCode2FA(e.target.value.replace(/\D/g, ''))}
              className="w-full px-4 py-3 sm:py-4 rounded-lg border border-input bg-background text-foreground text-center text-xl sm:text-2xl tracking-widest focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="000000"
              required
              autoFocus
              autoComplete="one-time-code"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || code2FA.length !== 6}
            className="w-full py-3 sm:py-3.5 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px] touch-manipulation"
          >
            {isLoading && <Loader2 className="animate-spin" size={20} />}
            Verifica
          </button>

          <button
            type="button"
            onClick={() => {
              setRequires2FA(false);
              setCode2FA('');
            }}
            className="w-full py-3 text-sm text-muted-foreground hover:text-foreground active:text-foreground min-h-[44px] touch-manipulation"
          >
            Torna al login
          </button>
        </form>
      </div>
    );
  }

  // Form login standard
  return (
    <div className="bg-card rounded-lg border border-border shadow-sm p-4 sm:p-6 animate-fadeIn">
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-foreground">Accedi</h2>
        <p className="text-muted-foreground mt-2 text-sm sm:text-base">
          Inserisci le tue credenziali per accedere
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">
            Email
          </label>
          <div className="relative">
            <Mail
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={20}
            />
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-4 py-3 sm:py-3.5 rounded-lg border border-input bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="nome@esempio.it"
              required
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-2">
            Password
          </label>
          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              size={20}
            />
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-12 py-3 sm:py-3.5 rounded-lg border border-input bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground active:text-foreground p-1.5 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:underline active:underline py-1 touch-manipulation"
          >
            Password dimenticata?
          </Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 sm:py-3.5 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 active:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-h-[48px] touch-manipulation"
        >
          {isLoading && <Loader2 className="animate-spin" size={20} />}
          Accedi
        </button>
      </form>

      <div className="mt-6 pt-4 border-t border-border">
        <img
          src="/regione_emilia_romagna.png"
          alt="Il progetto è realizzato grazie ai Fondi europei della Regione Emilia-Romagna"
          className="w-full h-auto"
        />
      </div>
    </div>
  );
}

export default LoginPage;
